import React, { useState, useEffect } from 'react'
import { simpananBerjangkaService } from '../../services/simpananBerjangkaService'
import api from '../../services/api'

const formatCurrency = (value) => {
  if (!value) return '0'
  return new Intl.NumberFormat('id-ID').format(value)
}

const TENOR_OPTIONS = [
  { value: 'SB_01', label: '1 Bulan', bulan: 1 },
  { value: 'SB_03', label: '3 Bulan', bulan: 3 },
  { value: 'SB_06', label: '6 Bulan', bulan: 6 },
  { value: 'SB_12', label: '12 Bulan', bulan: 12 },
  { value: 'SB_24', label: '24 Bulan', bulan: 24 }
]

// Default bunga rates per tenor
const DEFAULT_BUNGA = {
  'SB_01': '6.00',
  'SB_03': '6.50',
  'SB_06': '7.00',
  'SB_12': '7.50',
  'SB_24': '8.00'
}

const METODE_BUNGA = [
  { value: 'CASH', label: 'Tunai (Cash)' },
  { value: 'MUTASI_SUKARELA', label: 'Mutasi ke Sukarela' }
]

function SimpananBerjangkaForm({ onClose, onSuccess }) {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [members, setMembers] = useState([])
  const [searchNBA, setSearchNBA] = useState('')
  const [selectedMember, setSelectedMember] = useState(null)

  const [formData, setFormData] = useState({
    nba: '',
    kodeJenis: 'SB_06',
    nettoTarget: '',
    metodeBunga: 'CASH',
    keterangan: ''
  })

  // Get bunga from tenor
  const getBungaForTenor = (kodeJenis) => {
    return DEFAULT_BUNGA[kodeJenis] || '0'
  }

  const [adminFeePreview, setAdminFeePreview] = useState(null)
  const [errors, setErrors] = useState({})

  // Search members by NBA
  useEffect(() => {
    const searchMembers = async () => {
      if (searchNBA.length < 2) {
        setMembers([])
        return
      }

      try {
        const res = await api.get('/members/search', { params: { q: searchNBA } })
        setMembers(res.data?.data || [])
      } catch (error) {
        console.error('Error searching members:', error)
        setMembers([])
      }
    }

    const debounce = setTimeout(searchMembers, 300)
    return () => clearTimeout(debounce)
  }, [searchNBA])

  // Calculate admin fee preview when nettoTarget or tenor changes
  useEffect(() => {
    const fetchPreview = async () => {
      if (!formData.nettoTarget || !formData.kodeJenis) {
        setAdminFeePreview(null)
        return
      }

      const tenor = TENOR_OPTIONS.find(t => t.value === formData.kodeJenis)
      if (!tenor) return

      try {
        const res = await simpananBerjangkaService.previewAdminFee(
          parseFloat(formData.nettoTarget),
          tenor.bulan
        )
        setAdminFeePreview(res.data?.data)
      } catch (error) {
        console.error('Error fetching preview:', error)
        setAdminFeePreview(null)
      }
    }

    const debounce = setTimeout(fetchPreview, 300)
    return () => clearTimeout(debounce)
  }, [formData.nettoTarget, formData.kodeJenis])

  const handleMemberSelect = (member) => {
    setSelectedMember(member)
    setFormData({ ...formData, nba: member.NBA })
    setMembers([])
    setSearchNBA(member.NBA)
    setErrors({ ...errors, nba: '' })
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
    setErrors({ ...errors, [name]: '' })
  }

  const validate = () => {
    const newErrors = {}

    if (!formData.nba) {
      newErrors.nba = 'Pilih anggota terlebih dahulu'
    }
    if (!formData.nettoTarget || parseFloat(formData.nettoTarget) <= 0) {
      newErrors.nettoTarget = 'Masukkan jumlah saldo yang diinginkan'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validate()) return

    setSaving(true)
    try {
      await simpananBerjangkaService.createAccount({
        nba: formData.nba,
        kodeJenis: formData.kodeJenis,
        nettoTarget: parseFloat(formData.nettoTarget),
        bungaPersen: parseFloat(getBungaForTenor(formData.kodeJenis)),
        metodeBunga: formData.metodeBunga,
        keterangan: formData.keterangan
      })

      alert('Rekening Simpanan Berjangka berhasil dibuat!')
      onSuccess()
    } catch (error) {
      alert(error.response?.data?.message || 'Gagal membuat rekening')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header bg-primary text-white">
            <h5 className="modal-title">Pembukaan Simpanan Berjangka Baru</h5>
            <button type="button" className="close text-white" onClick={onClose}>
              <span>&times;</span>
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {/* Member Selection */}
              <div className="form-group">
                <label>Nomor Anggota (NBA) <span className="text-danger">*</span></label>
                {selectedMember ? (
                  <div className="alert alert-success py-2 mb-0">
                    <strong>{selectedMember.NBA}</strong> - {selectedMember.Nama}
                    <button
                      type="button"
                      className="btn btn-sm btn-link p-0 ml-2"
                      onClick={() => {
                        setSelectedMember(null)
                        setFormData({ ...formData, nba: '' })
                        setSearchNBA('')
                      }}
                    >
                      Ganti
                    </button>
                  </div>
                ) : (
                  <>
                    <input
                      type="text"
                      className={`form-control ${errors.nba ? 'is-invalid' : ''}`}
                      placeholder="Ketik NBA atau Nama..."
                      value={searchNBA}
                      onChange={(e) => setSearchNBA(e.target.value)}
                    />
                    {errors.nba && <div className="invalid-feedback">{errors.nba}</div>}
                    {members.length > 0 && (
                      <div className="list-group mt-1" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                        {members.map((m) => (
                          <button
                            key={m.NBA}
                            type="button"
                            className="list-group-item list-group-item-action py-2"
                            onClick={() => handleMemberSelect(m)}
                          >
                            <strong>{m.NBA}</strong> - {m.Nama}
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Tenor */}
              <div className="form-row">
                <div className="form-group col-md-6">
                  <label>Tenor <span className="text-danger">*</span></label>
                  <select
                    name="kodeJenis"
                    className="form-control"
                    value={formData.kodeJenis}
                    onChange={handleChange}
                  >
                    {TENOR_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                {/* Saldo Target */}
                <div className="form-group col-md-6">
                  <label>Saldo Bersih yang Diinginkan <span className="text-danger">*</span></label>
                  <div className="input-group">
                    <div className="input-group-prepend">
                      <span className="input-group-text">Rp</span>
                    </div>
                    <input
                      type="number"
                      name="nettoTarget"
                      className={`form-control ${errors.nettoTarget ? 'is-invalid' : ''}`}
                      placeholder="10.000.000"
                      value={formData.nettoTarget}
                      onChange={handleChange}
                      min="0"
                    />
                    {errors.nettoTarget && <div className="invalid-feedback">{errors.nettoTarget}</div>}
                  </div>
                </div>
              </div>

              {/* Admin Fee Preview */}
              {adminFeePreview && (
                <div className="alert alert-warning">
                  <div className="row">
                    <div className="col-md-4">
                      <small>Saldo Bersih:</small>
                      <div className="font-weight-bold">Rp {formatCurrency(adminFeePreview.nettoTarget)}</div>
                    </div>
                    <div className="col-md-4">
                      <small>Biaya Admin:</small>
                      <div className="font-weight-bold text-danger">Rp {formatCurrency(adminFeePreview.adminFee)}</div>
                    </div>
                    <div className="col-md-4">
                      <small>Total yang Dibayar:</small>
                      <div className="font-weight-bold text-success">Rp {formatCurrency(adminFeePreview.grossAmount)}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Bunga (auto from tenor) & Metode */}
              <div className="form-row">
                <div className="form-group col-md-6">
                  <label>Suku Bunga (% per Tahun)</label>
                  <div className="form-control bg-light">
                    <strong>{getBungaForTenor(formData.kodeJenis)}%</strong>
                    <small className="text-muted ml-2">(standar per tenor {TENOR_OPTIONS.find(t => t.value === formData.kodeJenis)?.label})</small>
                  </div>
                </div>

                <div className="form-group col-md-6">
                  <label>Metode Pencairan Bunga</label>
                  <select
                    name="metodeBunga"
                    className="form-control"
                    value={formData.metodeBunga}
                    onChange={handleChange}
                  >
                    {METODE_BUNGA.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Keterangan */}
              <div className="form-group">
                <label>Keterangan</label>
                <textarea
                  name="keterangan"
                  className="form-control"
                  rows="2"
                  value={formData.keterangan}
                  onChange={handleChange}
                ></textarea>
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Batal
              </button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-save"></i>}
                {' '}{saving ? 'Menyimpan...' : 'Simpan'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default SimpananBerjangkaForm